/* ============================================================================
   PANNEAU « CE QUE J'AI COMPRIS » (architecture v2, étape 4)
   ----------------------------------------------------------------------------
   Quand le schéma a été dessiné depuis une ANALYSE STRUCTURÉE, ce panneau
   l'affiche en clair (résumé) et permet de la CORRIGER : format simple/double,
   coups et acteurs (filet), effectif et phase (invasion), éléments (parcours).
   « Redessiner » repasse par sceneFromAnalysis en LOCAL — zéro appel IA,
   zéro quota consommé. Les menus proposent exactement les valeurs acceptées
   par la validation (ANALYSE_ENUMS).
   ========================================================================== */
import { ANALYSE_ENUMS as E } from "./analysis.js";

const $ = (id) => document.getElementById(id);
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const COUP_LABEL = { service: "Service", remise: "Remise", degagement: "Dégagement", amorti: "Amorti", bloc: "Bloc", poussette: "Poussette", manchette: "Manchette", echange: "Échange", reception: "Réception", passe: "Passe", smash: "Smash", kill: "Kill", topspin: "Top-spin", attaque: "Attaque" };
const PROF_LABEL = { fond: "au fond", median: "mi-court", avant: "court (filet)" };
const DIR_LABEL = { gauche: "à gauche", droite: "à droite", centre: "au centre", croise: "croisé", parallele: "parallèle" };
const ACT_LABEL = { passe: "Passe", course: "Course", dribble: "Conduite", tir: "Tir", centre: "Centre", ecran: "Écran" };
const DEPART_LABEL = { camp_propre: "camp propre", ligne_mediane: "ligne médiane", camp_adverse: "camp adverse" };

let onApply = null;   // callback main.js : (analyse) => boolean (re-rendu local)
let ctx = null;       // { analyse, sport, text } de la dernière génération

export function initUnderstanding(applyCb) {
  onApply = applyCb;
  $("understandToggle").addEventListener("click", () => {
    const b = $("understandBody");
    b.hidden = !b.hidden;
    $("understandToggle").textContent = b.hidden ? "✏️ Corriger" : "Fermer";
    if (!b.hidden && ctx) buildForm();
  });
}

/* Affiche (ou masque) le panneau pour l'analyse de la génération courante. */
export function showUnderstanding(analysisState) {
  ctx = analysisState && analysisState.analyse ? analysisState : null;
  const panel = $("understandPanel");
  if (!panel) return;
  if (!ctx) { panel.hidden = true; return; }
  panel.hidden = false;
  $("understandSummary").textContent = summary(ctx.analyse);
  const b = $("understandBody");
  if (!b.hidden) buildForm();
}

function summary(a) {
  if (a.famille === "filet") {
    const seq = (a.coups || []).map((c) =>
      (COUP_LABEL[c.coup] || c.coup) + (c.acteur ? ` (${c.acteur === "adversaire" ? "adv." : c.acteur})` : "") + ((+c.repetitions || 1) > 1 ? ` ×${c.repetitions}` : "")
    ).join(" → ");
    const fmt = a.format === "equipes" && a.effectif
      ? `${a.effectif.gauche} contre ${a.effectif.droite}`
      : a.format;
    return `Filet · ${fmt} · ${seq}`;
  }
  if (a.famille === "invasion") {
    const e = a.effectif || {};
    return `Invasion · ${e.attaquants} contre ${e.defenseurs}${e.gardien ? " + gardien" : ""} · ${(a.phase || []).length} temps` +
      (a.depart ? ` · départ ${DEPART_LABEL[a.depart] || a.depart}` : "");
  }
  if (a.famille === "parcours") return `Parcours · ${(a.elements || []).map((el) => el.nom).join(" → ")}`;
  if (a.famille === "atelier") return `Ateliers · ${(a.ateliers || []).length}${a.rotation ? " · rotation" : ""} : ${(a.ateliers || []).map((el) => el.nom).join(" · ")}`;
  return "";
}

/* ----------------------------- Formulaires ------------------------------- */
function selHtml(cls, options, value, labels = {}, nullLabel = null) {
  const opts = (nullLabel != null ? [`<option value="">${esc(nullLabel)}</option>`] : [])
    .concat(options.map((o) => `<option value="${esc(o)}"${o === value ? " selected" : ""}>${esc(labels[o] || o)}</option>`));
  return `<select class="${cls}">${opts.join("")}</select>`;
}

function filetRow(c = {}) {
  return `<div class="u-row" data-row>
    ${selHtml("u-acteur", E.acteursFilet, c.acteur || "", { adversaire: "Adversaire" }, "— qui ? auto —")}
    ${selHtml("u-coup", E.coups, c.coup || "remise", COUP_LABEL)}
    ${selHtml("u-prof", E.profondeurs, c.profondeur || "", PROF_LABEL, "— profondeur —")}
    ${selHtml("u-dir", E.directions, c.direction || "", DIR_LABEL, "— direction —")}
    <input class="u-rep" type="number" min="1" max="10" value="${+c.repetitions || 1}" title="Répétitions (ex : 10 échanges)">
    <button class="iconbtn u-del" data-del title="Retirer ce coup">🗑️</button>
  </div>`;
}
function invActors(e) {
  const list = [];
  for (let i = 1; i <= Math.min(11, +e.attaquants || 0); i++) list.push("A" + i);
  for (let i = 1; i <= Math.min(11, +e.defenseurs || 0); i++) list.push("D" + i);
  if (e.gardien) list.push("gardien");
  return list;
}
function invRow(st = {}, e) {
  const actors = invActors(e);
  return `<div class="u-row" data-row>
    ${selHtml("u-acteur", actors, st.acteur || actors[0] || "", { gardien: "Gardien" })}
    ${selHtml("u-action", E.actionsInvasion, st.action || "passe", ACT_LABEL)}
    ${selHtml("u-cible", actors.concat(["but"]), st.cible || "", { but: "But", gardien: "Gardien" }, "— cible : aucune —")}
    <input class="u-note" type="text" placeholder="libellé affiché (optionnel)" value="${esc(st.note || "")}">
    <button class="iconbtn u-del" data-del title="Retirer ce temps">🗑️</button>
  </div>`;
}
function parcoursRow(el = {}) {
  return `<div class="u-row" data-row>
    <input class="u-nom" type="text" maxlength="40" placeholder="Nom de l'élément (ex : Roulade avant)" value="${esc(el.nom || "")}">
    ${selHtml("u-dir", ["gauche", "droite"], el.direction || "", { gauche: "vers la gauche", droite: "vers la droite" }, "— tout droit —")}
    <button class="iconbtn u-del" data-del title="Retirer cet élément">🗑️</button>
  </div>`;
}
function atelierRow(el = {}) {
  return `<div class="u-row" data-row>
    <input class="u-nom" type="text" maxlength="60" placeholder="Nom de l'atelier (ex : Sprint 10 m)" value="${esc(el.nom || "")}">
    <button class="iconbtn u-del" data-del title="Retirer cet atelier">🗑️</button>
  </div>`;
}

function buildForm() {
  const a = ctx.analyse, b = $("understandBody");
  let inner = "";
  if (a.famille === "filet") {
    const eff = a.effectif || { gauche: 1, droite: 2 };
    const effInputs = a.format === "equipes"
      ? ` <label>Gauche (serveur) <input class="u-eg" type="number" min="1" max="3" value="${+eff.gauche || 1}"></label>
          <label>Droite <input class="u-ed" type="number" min="1" max="3" value="${+eff.droite || 2}"></label>`
      : "";
    inner = `<div class="u-line">Format
        ${selHtml("u-format", ["simple", "double", "equipes"], a.format, { simple: "Simple — A contre B", double: "Double — A et B contre C et D", equipes: "Équipes (volley, ex. 1 contre 2)" })}${effInputs}</div>
      <div class="u-rows" id="uRows">${(a.coups || []).map(filetRow).join("")}</div>
      <button class="sm" data-add>+ Ajouter un coup</button>`;
  } else if (a.famille === "invasion") {
    const e = a.effectif || {};
    inner = `<div class="u-line">
        <label>Attaquants <input class="u-na" type="number" min="1" max="11" value="${+e.attaquants || 2}"></label>
        <label>Défenseurs <input class="u-nd" type="number" min="0" max="11" value="${+e.defenseurs || 1}"></label>
        <label class="u-check"><input class="u-gk" type="checkbox"${e.gardien ? " checked" : ""}> Gardien</label>
        <label>Départ ${selHtml("u-depart", E.departs, a.depart || "", DEPART_LABEL, "— libre —")}</label>
      </div>
      <div class="u-rows" id="uRows">${(a.phase || []).map((st) => invRow(st, e)).join("")}</div>
      <button class="sm" data-add>+ Ajouter un temps</button>`;
  } else if (a.famille === "parcours") {
    inner = `<div class="u-rows" id="uRows">${(a.elements || []).map(parcoursRow).join("")}</div>
      <button class="sm" data-add>+ Ajouter un élément</button>`;
  } else if (a.famille === "atelier") {
    inner = `<div class="u-line">
        <label class="u-check"><input class="u-rot" type="checkbox"${a.rotation ? " checked" : ""}> Rotation des groupes entre ateliers</label>
      </div>
      <div class="u-rows" id="uRows">${(a.ateliers || []).map(atelierRow).join("")}</div>
      <button class="sm" data-add>+ Ajouter un atelier</button>`;
  }
  b.innerHTML = inner + `
    <div class="u-actions">
      <button class="primary sm" data-apply>↻ Redessiner le schéma</button>
      <span class="u-free">re-dessin local — ne consomme pas de quota</span>
    </div>
    <div class="error" id="uError"></div>`;

  // Délégation (affectation directe : pas d'empilement de listeners à chaque rebuild).
  b.onclick = (ev) => {
    const del = ev.target.closest("[data-del]");
    if (del) { del.closest("[data-row]").remove(); return; }
    if (ev.target.closest("[data-add]")) { addRow(); return; }
    if (ev.target.closest("[data-apply]")) { apply(); }
  };
  // L'effectif change -> les menus Acteur/Cible doivent proposer A1..An, D1..Dm ;
  // le format filet change -> afficher/masquer les effectifs « équipes ».
  b.onchange = (ev) => {
    if (ctx.analyse.famille === "invasion" && ev.target.matches(".u-na,.u-nd,.u-gk")) {
      ctx.analyse = collect();
      buildForm();
    } else if (ctx.analyse.famille === "filet" && ev.target.matches(".u-format")) {
      ctx.analyse = collect();
      buildForm();
    }
  };
}

function addRow() {
  const rows = $("uRows"), a = ctx.analyse;
  const tmp = document.createElement("div");
  tmp.innerHTML = a.famille === "filet" ? filetRow()
    : a.famille === "invasion" ? invRow({}, readEffectif())
    : a.famille === "atelier" ? atelierRow()
    : parcoursRow();
  rows.appendChild(tmp.firstElementChild);
}

function readEffectif() {
  const b = $("understandBody");
  return {
    attaquants: Math.max(1, Math.min(11, +b.querySelector(".u-na").value || 1)),
    defenseurs: Math.max(0, Math.min(11, +b.querySelector(".u-nd").value || 0)),
    gardien: b.querySelector(".u-gk").checked,
  };
}

/* Relit le formulaire -> objet `analyse` (même contrat que l'Edge Function). */
function collect() {
  const a = ctx.analyse, b = $("understandBody");
  const rows = Array.from(b.querySelectorAll("[data-row]"));
  if (a.famille === "filet") {
    const format = b.querySelector(".u-format").value;
    const eg = b.querySelector(".u-eg"), ed = b.querySelector(".u-ed");
    return {
      version: 1, famille: "filet",
      format,
      ...(format === "equipes" ? { effectif: { gauche: eg ? +eg.value || 1 : 1, droite: ed ? +ed.value || 2 : 2 } } : {}),
      coups: rows.map((r) => ({
        acteur: r.querySelector(".u-acteur").value || null,
        coup: r.querySelector(".u-coup").value,
        profondeur: r.querySelector(".u-prof").value || null,
        direction: r.querySelector(".u-dir").value || null,
        repetitions: Math.max(1, Math.min(10, +r.querySelector(".u-rep").value || 1)),
      })),
    };
  }
  if (a.famille === "invasion") {
    return {
      version: 1, famille: "invasion",
      effectif: readEffectif(),
      depart: b.querySelector(".u-depart").value || null,
      phase: rows.map((r) => ({
        acteur: r.querySelector(".u-acteur").value,
        action: r.querySelector(".u-action").value,
        cible: r.querySelector(".u-cible").value || null,
        note: r.querySelector(".u-note").value.trim() || null,
      })),
    };
  }
  if (a.famille === "atelier") {
    return {
      version: 1, famille: "atelier",
      rotation: b.querySelector(".u-rot").checked,
      ateliers: rows.map((r) => ({ nom: r.querySelector(".u-nom").value.trim() })),
    };
  }
  return {
    version: 1, famille: "parcours",
    elements: rows.map((r) => ({ nom: r.querySelector(".u-nom").value.trim(), direction: r.querySelector(".u-dir").value || null })),
  };
}

function apply() {
  const analyse = collect();
  const err = $("uError");
  const ok = onApply && onApply(analyse);
  if (!ok) {
    err.textContent = "Analyse incomplète ou incohérente — vérifie : au moins 2 coups (filet), acteurs C/D seulement en double, acteurs dans l'effectif, noms d'éléments non vides.";
    err.classList.add("active");
    return;
  }
  err.textContent = ""; err.classList.remove("active");
  ctx.analyse = analyse;
  $("understandSummary").textContent = summary(analyse);
}
